import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import LoadingProgressBar from '../../components/LoadingProgressBar';
import { useLoading } from '../../contexts/LoadingContext';
import '../../styles/styles.css';
import './dashboard-responsive.css';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuIcon from '@mui/icons-material/Menu';
import { useThemeMode } from '../../ThemeContext';
import PersonIcon from '@mui/icons-material/Person';
import { authAPI, dataAPI } from '../../helpers/api_service';
import { APP_URLS, getProfileCompanyPicUrl } from '../../helpers/url_helper';
import sessionManager from '../../helpers/session_manager';
import { handleOAuthLogout, detectOAuthProvider, getOAuthAccessToken, clearAllOAuthData } from '../../helpers/oauth_logout';
import Notifications from '../Notifications/Notifications';

// Helper function to handle profile picture URL
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  
  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic; // Return as is if it's already a full URL
  } else {
    return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
  }
};

function DashboardLayout() {
  const { mode, toggleTheme } = useThemeMode();
  const { isLoading, loadingMessage } = useLoading();
  const themeClass = mode === 'dark' ? 'dashboard-dark' : 'dashboard-light';
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for user data
  const [userFullName, setUserFullName] = useState('');
  const [userProfilePic, setUserProfilePic] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Get page title based on current route
  const getPageTitle = () => {
    if (location.pathname === '/dashboard') {
      return 'Dashboard';
    }
    if (location.pathname === '/assistant') {
      return 'Complimate AI';
    }
    if (location.pathname === '/projects') {
      return 'Projects';
    }
    if (location.pathname === '/manage-task') {
      return 'Manage Task';
    }
    if (location.pathname === '/documents') {
      return 'Documents';
    }
    if (location.pathname === APP_URLS.COMPANY_FORMS) {
      return 'Company Form';
    }
    if (location.pathname.startsWith(APP_URLS.COMPANY_FORM_FILL)) {
      return 'Fill Company Form';
    }
    if (location.pathname.startsWith(APP_URLS.COMPANY_FORM_RECORDS)) {
      return 'Company Form Records';
    }
    if (location.pathname.startsWith(APP_URLS.COMPANY_FORM_RECORD_VIEW)) {
      return 'Form Record Details';
    }
    if (location.pathname === '/manage-employee/employees') {
      return 'Employees';
    }
    if (location.pathname.startsWith('/manage-employee/employees/')) {
      return 'Edit Profile';
    }
    if (location.pathname === '/manage-employee/roles') {
      return 'Roles';
    }
    if (location.pathname === '/manage-employee/role-permission') {
      return 'Role Permissions';
    }
    if (location.pathname === '/subscription' || location.pathname.startsWith('/subscription')) {
      return 'Subscription';
    }
    if (location.pathname === '/profile') {
      return 'Profile';
    }
    if (location.pathname === '/settings') {
      return 'Settings';
    }
    if (location.pathname === APP_URLS.DIGITAL_FORM_MANAGEMENT) {
      return 'Digital form Library';
    }
    if (location.pathname === APP_URLS.DIGITAL_FORM_USE_TEMPLATE) {
      return 'Create Form';
    }
    if (location.pathname === '/checkout') {
      return 'Subscription';
    }
    if (location.pathname === '/payment-success') {
      return 'Subscription';
    }
    if (location.pathname === '/payment-cancel') {
      return 'Subscription';
    }
    
    // Handle project-specific routes
    if (location.pathname.includes('/project/') && location.pathname.includes('/tasks')) {
      return 'Project Tasks';
    }
    if (location.pathname.includes('/project/') && location.pathname.includes('/task/')) {
      return 'Task Details';
    }
    
    return 'Dashboard';
  };


  // Fetch notifications when notification icon is clicked
  const fetchNotifications = async () => {
    try {
      const response = await dataAPI.getNotifications({ showLoading: false });
      if (response.status === 'success') {
        setNotifications(response.data || []);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    }
  };

  // Handle notification icon click
  const handleNotificationClick = () => {
    // SIMPLE: Only toggle sidebar, NO API calls
    setShowNotifications(!showNotifications);
  };

  // Callback for refreshing notifications from within the sidebar
  const handleRefreshNotifications = async () => {
    try {
      const response = await dataAPI.getNotifications({ showLoading: false });
      
      if (response.status === 'success') {
        setNotifications(response.data || []);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('🔧 DashboardLayout: Error refreshing notifications:', error);
      setNotifications([]);
    }
  };
  
  // Load user data from localStorage on component mount
  useEffect(() => {
    const getUserData = () => {
      const fullName = localStorage.getItem('userFullName') || '';
      const profilePic = localStorage.getItem('userProfilePic') || '';
      setUserFullName(fullName);
      setUserProfilePic(profilePic);
    };

    getUserData();

    // Listen for profile updates
    const handleProfileUpdate = (event) => {
      const updatedUserData = event.detail;
      setUserFullName(updatedUserData.full_name || updatedUserData.user_full_name || '');
      setUserProfilePic(updatedUserData.profile_pic || updatedUserData.user_profile_pic || '');
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the options menu and notifications
      const optionsMenu = event.target.closest('[data-options-menu]');
      const notificationsMenu = event.target.closest('[data-notifications-menu]');
      const optionsButton = event.target.closest('[data-options-button]');
      const notificationsButton = event.target.closest('[data-notifications-button]');
      
      if (showOptionsMenu && !optionsMenu && !optionsButton) {
        setShowOptionsMenu(false);
      }
      
      if (showNotifications && !notificationsMenu && !notificationsButton) {
        setShowNotifications(false);
      }
      if (sidebarOpen) {
        const sidebarEl = event.target.closest('.sidebar');
        const hamburgerEl = event.target.closest('.dashboard-hamburger-btn');
        if (!sidebarEl && !hamburgerEl) {
          setSidebarOpen(false);
        }
      }
    };

    if (showNotifications || showOptionsMenu || sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, showOptionsMenu, sidebarOpen]);

  // Close mobile sidebar when route changes (e.g. after navigation from Sidebar)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Handle logout
  const handleLogout = async () => {
    try {
      // Close the options menu first
      setShowOptionsMenu(false);
      
      // Detect OAuth provider and handle OAuth logout first
      const oauthProvider = detectOAuthProvider();
      if (oauthProvider) {
        const accessToken = getOAuthAccessToken(oauthProvider);
        await handleOAuthLogout(oauthProvider, accessToken);
      }
      
      // Then handle backend logout
      const token = localStorage.getItem('authToken');
      if (token) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Still proceed with local cleanup even if API calls fail
    } finally {
      // Clear all OAuth data
      clearAllOAuthData();
      
      // Use session manager to end session
      sessionManager.endSession();
    }
  };

  return (
    <div className={`dashboard-root ${themeClass}`}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideInRight {
            from { 
              transform: translateX(100%); 
              opacity: 0;
            }
            to { 
              transform: translateX(0); 
              opacity: 1;
            }
          }
        `}
      </style>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="dashboard-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setSidebarOpen(false)}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
        />
      )}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: 101,
        }}
      />
      <main className="main-content dashboard-main-content" style={{ marginLeft: 235, position: 'relative' }}>
        {/* Top Bar (fixed) */}
        <div className="dashboard-header" style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: mode === 'dark' ? '#0f0f0f' : '#f8f9fa',
        }}>
          <button
            type="button"
            className="dashboard-hamburger-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            style={{
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              marginRight: 12,
              border: 'none',
              borderRadius: 10,
              background: mode === 'dark' ? '#232323' : '#e8e8e8',
              color: mode === 'dark' ? '#fff' : '#333',
              cursor: 'pointer',
            }}
          >
            <MenuIcon style={{ fontSize: 24 }} />
          </button>
          <div className="header-title">{getPageTitle()}</div>
          <div style={{ flex: 1 }} />
          {/* Theme Toggle Button */}
          <button
            className="header-theme-toggle-btn"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: mode === 'dark' ? '#232323' : '#e0f7f7',
              border: 'none',
              borderRadius: 999,
              padding: '6px 18px',
              marginRight: 24,
              cursor: 'pointer',
              transition: 'background 0.2s',
              boxShadow: mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.15)' : '0 2px 8px rgba(38,195,192,0.10)'
            }}
          >
            {mode === 'dark' ? <Brightness7Icon style={{ color: '#2EDFCB' }} /> : <Brightness4Icon style={{ color: '#009391' }} />}
            <span style={{ color: mode === 'dark' ? '#fff' : '#009391', fontWeight: 500, fontSize: 16 }}>
              {mode === 'light' ? 'Dark' : 'Light'}
            </span>
          </button>
          <div className="header-user-group" style={{ minHeight: '45px', height: '45px', alignItems: 'center', display: 'flex' }}>
            {/* Notification Icon - moved before username */}
            <button 
              className="header-icon-btn"
              data-notifications-button
              onClick={handleNotificationClick}
              style={{ 
                position: 'relative',
                background: showNotifications ? '#22B0AD' : '#009391',
                border: 'none',
                borderRadius: '13px',
                width: '33px',
                height: '33px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                marginRight: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'background-color 0.2s ease, transform 0.1s ease',
                transform: 'scale(1)'
              }}
              onMouseDown={(e) => {
                e.target.style.transform = 'scale(0.95)';
              }}
              onMouseUp={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              <NotificationsIcon 
                style={{ 
                  color: '#fff',
                  fontSize: '23px',
                }} 
              />
              {/* Notification badge */}
              {notifications.filter(n => !n.is_read).length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  zIndex: 2
                }}>
                  {notifications.filter(n => !n.is_read).length}
                </div>
              )}
            </button>

            {/* User Info */}
            <div className="header-user" style={{ display: 'flex', alignItems: 'center', minWidth: 0, height: '37px', marginRight: '12px' }}>
              <div 
                className="user-avatar"
                style={{
                  backgroundImage: userProfilePic ? `url(${getProfilePicUrl(userProfilePic)})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  width: '33px',
                  height: '33px',
                  borderRadius: '50%',
                  marginRight: '8px',
                  flexShrink: 0,
                  backgroundColor: !userProfilePic ? '#009391' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {!userProfilePic && (
                  <PersonIcon style={{ color: '#fff', fontSize: '22px' }} />
                )}
              </div>
              <div className="user-name" style={{
                fontWeight: 500,
                fontSize: '20px',
                color: mode === 'dark' ? '#fff' : '#222',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
                maxWidth: '125px',
                flexShrink: 1,
                lineHeight: '37px',
                height: '37px'
              }}>{userFullName || 'User'}</div>
            </div>

            {/* Triple Dot Options Menu */}
            <div style={{ position: 'relative' }}>
              <button 
                className="header-icon-btn"
                data-options-button
                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: '33px',
                  height: '33px',
                  border: 'none',
                  background: '#009391',
                  borderRadius: '13px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0
                }}
              >
                <MoreVertIcon style={{ 
                  fontSize: '23px',
                  color: '#fff',
                }} />
              </button>

              {/* Options Menu Dropdown */}
              {showOptionsMenu && (
                <div 
                  data-options-menu
                  style={{
                    position: 'absolute',
                    top: '45px',
                    right: '0px',
                    backgroundColor: mode === 'dark' ? '#333' : '#fff',
                    border: `1px solid ${mode === 'dark' ? '#555' : '#ddd'}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    padding: '8px 0',
                    minWidth: '160px',
                    zIndex: 1000
                  }}>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOptionsMenu(false);
                      navigate('/profile');
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'transparent',
                      color: mode === 'dark' ? '#fff' : '#333',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s',
                      borderBottom: `1px solid ${mode === 'dark' ? '#555' : '#f0f0f0'}`
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <PersonIcon style={{ fontSize: '18px' }} />
                    View Profile
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLogout();
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'transparent',
                      color: mode === 'dark' ? '#fff' : '#333',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <PowerSettingsNewIcon style={{ fontSize: '18px' }} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Notifications Sidebar */}
        <Notifications 
          showNotifications={showNotifications} 
          setShowNotifications={setShowNotifications}
          notifications={notifications}
          onRefresh={() => handleRefreshNotifications()}
        />
        
        {/* Spacer for fixed topbar */}
        {/* <div style={{ height: 85, minHeight: 85 }} /> */}
        {/* Main Content Area - dashboard-outlet-wrapper used for mobile overflow containment */}
        <div className="dashboard-outlet-wrapper" style={{ position: 'relative', minHeight: 'calc(100vh - 60px)' }}>
          <Outlet />
          <LoadingProgressBar isVisible={isLoading} message={loadingMessage} />
        </div>
      </main>
    </div>
  );
}

export default DashboardLayout; 